import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getRecentActivity, type ActivityEvent } from "@/api/adminApi";

const EVENT_TYPES = [
  { key: "", label: "All" },
  { key: "signup", label: "Signups" },
  { key: "purchase", label: "Purchases" },
  { key: "generation_failure", label: "Failures" },
  { key: "reward", label: "Rewards" },
] as const;

const BADGE_CLASS: Record<string, string> = {
  signup: "badge-gold",
  purchase: "badge-success",
  generation_failure: "badge-danger",
  reward: "badge-muted",
};

const TYPE_LABEL: Record<string, string> = {
  signup: "Signup",
  purchase: "Purchase",
  generation_failure: "Failure",
  reward: "Reward",
};

function timeAgo(d: string): string {
  const seconds = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function ActivityPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const types = filter ? [filter] : undefined;
      const res = await getRecentActivity(0, 50, types);
      setEvents(res.events);
      setHasMore(res.has_more);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const types = filter ? [filter] : undefined;
      const res = await getRecentActivity(events.length, 50, types);
      setEvents((prev) => [...prev, ...res.events]);
      setHasMore(res.has_more);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div>
      {/* Filter bar */}
      <div className="filter-bar">
        {EVENT_TYPES.map((t) => (
          <button
            key={t.key}
            className={`range-btn ${filter === t.key ? "active" : ""}`}
            onClick={() => { setFilter(t.key); setEvents([]); }}
          >
            {t.label}
          </button>
        ))}
        <button
          className="btn-outline"
          onClick={load}
          disabled={loading}
          style={{ marginLeft: "auto" }}
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {!loading && events.length > 0 && (
        <div style={{ fontSize: 11, color: "#9A8E7A", marginBottom: 8 }}>
          Showing {events.length} event{events.length === 1 ? "" : "s"}{hasMore ? " (more available)" : ""}
        </div>
      )}

      {loading && <div className="loading">Scanning the reels...</div>}

      {!loading && events.length === 0 && (
        <div className="empty-state">No recent activity</div>
      )}

      {!loading && events.length > 0 && (
        <div className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>User</th>
                <th>Description</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id}>
                  <td>
                    <span className={`badge ${BADGE_CLASS[e.type] ?? "badge-muted"}`}>
                      {TYPE_LABEL[e.type] ?? e.type}
                    </span>
                  </td>
                  <td>
                    <span
                      style={{ color: "var(--gold)", cursor: "pointer" }}
                      onClick={() => navigate(`/users/${e.user_id}`)}
                    >
                      {e.user_email}
                    </span>
                  </td>
                  <td>{e.description}</td>
                  <td style={{ whiteSpace: "nowrap", color: "var(--text-secondary)" }}>
                    {timeAgo(e.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && hasMore && (
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <button className="btn-outline" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? "Loading..." : "Load More"}
          </button>
        </div>
      )}
    </div>
  );
}
